/**
 * @file Invite owners to manage the owner dashboard for the selected relation
 * @author Max van Loosbroek
 */

import React, { useCallback, useEffect, useState } from 'react'
import {
  TbDashDetailedColumn,
  TbDashDetailedItem,
  TbDashDetailedItemRow,
  TbDashDialog,
  TbDashErrorDialog
} from 'tinybots-react-components'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../common/redux/root.reducer'
import { AppDispatch } from '../../../../common/redux/store'
import {
  clearErrorsInvite,
  clearSearch
} from '../redux/reducer'
import { inviteOwner } from '../redux/thunks'
import { CircularProgress } from '@material-ui/core'
import { usePrevious } from 'tinybots-react-components/lib/hooks'

export interface InviteOwnerProps {
  relationId: number | null
}

export const InviteOwner = ({ relationId }: InviteOwnerProps) => {
  const [email, setEmail] = useState('')
  const [success, setSuccess] = useState(false)
  const previousRelationId = usePrevious(relationId)
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    if (previousRelationId !== relationId) {
      setEmail('')
      dispatch(clearSearch())
    }
  }, [relationId])

  const { getting, getError, patchError, patching } = useSelector<
  RootState,
  {
    patching: boolean
    patchError: null | string
    getting: boolean
    getError: null | string
  }
  >(state => state.organisationAccounts.invite)

  const errorMessage = getError || patchError

  const onEmailChange = useCallback((value: string | number | boolean) => {
    setEmail(String(value))
  }, [])

  const submit = useCallback(() => {
    if (relationId == null) return
    void dispatch(inviteOwner({ email, relationId })).then(() => {
      setSuccess(true)
    })
  }, [email, relationId])

  return getting || patching ? (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 340
      }}
    >
      <CircularProgress size={100} />
    </div>
  ) : (
    <>
      <TbDashDetailedColumn>
        <TbDashDetailedItemRow>
          <h2
            style={{
              paddingLeft: 16,
              margin: 0,
              marginBottom: 20,
              fontSize: 16
            }}
          >
            Send invite for dashboard
          </h2>
        </TbDashDetailedItemRow>
        <TbDashDetailedItemRow>
          <TbDashDetailedItem
            type='text'
            data-testid='email-search'
            label='Email address'
            value={email}
            onChange={onEmailChange}
          />
          <TbDashDetailedItem
            disabled={!email}
            type='button'
            label=''
            value='SEND INVITE'
            onClick={submit}
          />
        </TbDashDetailedItemRow>
      </TbDashDetailedColumn>
      <TbDashErrorDialog
        message={errorMessage ?? 'Unknown error'}
        open={!!errorMessage}
        handleClose={() => {
          dispatch(clearErrorsInvite())
        }}
      />
      <TbDashDialog
        open={success}
        title={'Invite sent'}
        message={'Your invite has been sent successfully'}
        submitButton='CONTINUE'
        handleSubmit={() => {
          dispatch(clearSearch())
          setSuccess(false)
          setEmail('')
        }}
        handleClose={() => {}}
        color={'#ba9a1b'}
      />
    </>
  )
}
